import { ConfigResponse } from '../config/config.types'
import {
  convertHexToRGB,
  convertRgbOrRgbaToHex,
  convertRgbToRGBA,
  isHexColor,
} from '../util/colors'
import { getBaseUri } from '../util/script-params'

interface DAOCache {
  config: Record<string, ConfigResponse>
}

interface DAOProps {
  win: Window
}

interface ConfigAmount {
  name: string
  amount: number
  isVisible: boolean
  isDefault: boolean
}
export default class DAO {
  private props: DAOProps
  // sdk-config contains pgdid-scoped decisions, so cache it only for this SDK lifecycle.
  private cache: DAOCache = { config: {} }
  private destroyed = false

  constructor(props: DAOProps) {
    this.props = props
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }
    this.destroyed = true
    this.cache.config = {}
  }

  public fetchConfigs = async (
    campaignIds: string[],
    pgdid: string | null
  ): Promise<ConfigResponse[]> => {
    const promises = campaignIds.map((id) => this.fetchConfig(id, pgdid))
    return Promise.allSettled(promises).then((results) => {
      results
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .forEach((result, index) => {
          console.error(`Failed to fetch campaign ${campaignIds[index]} failed:`, result.reason)
        })
      return results
        .filter(
          (result): result is PromiseFulfilledResult<ConfigResponse> =>
            result.status === 'fulfilled'
        )
        .map((result) => result.value)
    })
  }

  public async fetchConfig(campaignId: string, pgdid?: string | null): Promise<ConfigResponse> {
    if (this.cache.config[campaignId]) {
      return this.cache.config[campaignId]
    }

    /**
     * TODO: Once https://classydev.atlassian.net/browse/CL-46263 is completed
     * we'll update this logic to set the selected currency.
     */
    const currency = 'USD'
    const screenHeight = window?.screen?.height || 0
    const screenWidth = window?.screen?.width || 0
    const locale = navigator.language
    const queryParams = new URLSearchParams({
      published: '1',
      clickSource: window?.location?.origin,
      screenHeight: String(screenHeight),
      screenWidth: String(screenWidth),
      locale,
      currency,
    })
    if (pgdid) {
      queryParams.append('pgdid', pgdid)
    }

    const hostParams = new URLSearchParams(this.props.win.location.search)
    for (const param of ['utm_medium', 'utm_source']) {
      const value = hostParams.get(param)
      // APIv2 rejects values over 255 Unicode characters, losing the suggested amounts.
      if (value && Array.from(value).length <= 255) {
        queryParams.set(param, value)
      }
    }

    // Endpoints are served from the monolith origin (e.g. https://giving.gofundme.com).
    const apiBaseUri = new URL(getBaseUri()).origin
    const response = await this.props.win.fetch(
      `${apiBaseUri}/api/checkout/sdk-config/campaigns/${campaignId}?${queryParams}`
    )

    if (!response.ok) {
      throw new Error('Failed to fetch config')
    }

    const configData = await response.json()
    this._setCampaignLocale(configData.data.locale || 'en')
    let config = this._adjustConfigTheme(configData.data)
    config = this._adjustConfigWithIAA(config)
    this.cache.config[campaignId] = config

    return config
  }

  // PS. Mutates the config
  private _adjustConfigTheme = (config: ConfigResponse) => {
    const theme = config.theme
    const primaryColor = theme['--theme-color__brand-primary']
    const secondaryColor = primaryColor
      ? isHexColor(primaryColor)
        ? convertRgbToRGBA(convertHexToRGB(primaryColor), 0.1)
        : convertRgbToRGBA(primaryColor, 0.1)
      : ''

    for (const key in config.components) {
      const component = config.components[key]

      if (component.type === 'inline-donation-grid') {
        const props = component.props

        if (props && !props.primaryColor) {
          props.primaryColor = primaryColor // Update primaryColor if empty
        }

        if (props && !props.secondaryColor) {
          props.secondaryColor = convertRgbOrRgbaToHex(secondaryColor) // Update secondaryColor if empty
        }
      }
    }

    return config
  }

  private _getIAATiles = (iaaAmounts: number[], hasOtherValue: boolean) => {
    const configAmountsWithIAA: ConfigAmount[] = []
    for (let i = 0; i < iaaAmounts.length; i++) {
      configAmountsWithIAA.push({
        name: `preset${i + 1}`,
        amount: iaaAmounts[i],
        isVisible: true,
        isDefault: false,
      })
    }

    if (hasOtherValue) {
      configAmountsWithIAA.push({
        name: 'other',
        amount: 0,
        isVisible: true,
        isDefault: false,
      })
    }

    return configAmountsWithIAA
  }

  private _adjustDefaultFrequency = (
    configFrequencies: ConfigAmount[],
    isDefaultRecurring?: boolean
  ) => {
    configFrequencies.forEach((frequency) => {
      if (frequency.name === 'one-time') {
        frequency.isDefault = !isDefaultRecurring
      }
      if (frequency.name !== 'one-time' && frequency.isVisible) {
        frequency.isDefault = isDefaultRecurring ?? false
      }
    })
  }

  // PS. Mutates the config
  private _adjustConfigWithIAA = (config: ConfigResponse) => {
    const iaaValues = config.iaa
    if (iaaValues.status !== 200) {
      return config
    }

    if (
      config.iaaConfig.useIntelligentAskOnetime === true ||
      config.intelligentAsksConfig?.is_enabled
    ) {
      for (const key in config.components) {
        const component = config.components[key]
        if (component.type === 'inline-donation-grid') {
          const props = component.props
          props.donationAmounts = this._getIAATiles(
            iaaValues.data.suggestedOnetimeAmounts ?? [],
            Boolean(props.donationAmounts.find((tile: ConfigAmount) => tile.name === 'other'))
          )
          if (config.intelligentAsksConfig?.is_enabled) {
            this._adjustDefaultFrequency(
              props.donationFrequencies,
              iaaValues.data.isDefaultRecurring
            )
          }
        }
        if (component.type === 'checkout' && component.sections) {
          for (const key in component.sections) {
            if (component.sections[key].type === 'donation-form') {
              const props = component.sections[key].props
              props.donationAmounts = this._getIAATiles(
                iaaValues.data.suggestedOnetimeAmounts ?? [],
                Boolean(props.donationAmounts.find((tile: ConfigAmount) => tile.name === 'other'))
              )
              if (config.intelligentAsksConfig?.is_enabled) {
                this._adjustDefaultFrequency(
                  props.donationFrequencies,
                  iaaValues.data.isDefaultRecurring
                )
              }
            }
          }
        }
      }
    }

    if (
      config.iaaConfig.useIntelligentAskRecurring === true ||
      config.intelligentAsksConfig?.is_enabled
    ) {
      for (const key in config.components) {
        const component = config.components[key]
        if (component.type === 'inline-donation-grid') {
          const props = component.props
          props.recurringAmounts = this._getIAATiles(
            iaaValues.data.suggestedRecurringAmounts ?? [],
            Boolean(props.recurringAmounts.find((tile: ConfigAmount) => tile.name === 'other'))
          )
        }
        if (component.type === 'checkout' && component.sections) {
          for (const key in component.sections) {
            if (component.sections[key].type === 'donation-form') {
              const props = component.sections[key].props
              props.recurringAmounts = this._getIAATiles(
                iaaValues.data.suggestedRecurringAmounts ?? [],
                Boolean(props.recurringAmounts.find((tile: ConfigAmount) => tile.name === 'other'))
              )
            }
          }
        }
      }
    }

    return config
  }

  private _setCampaignLocale = (locale: string) => {
    document.cookie = `i18next=${locale}`
  }
}
